// Ops bot — Telegram chat bindings overview (Wave 9.3).
//
// One read-mostly screen over GET /admin/ops-chats: every chat binding (active
// AND inactive) across clients, agencies, and the admin singleton, plus the
// admin-singleton binding editor itself (the client/agency editors live on
// their detail surfaces). Owner names are resolved from the same /admin/clients
// and /admin/agencies lists the CRM screen uses — display only; the backend
// authorizes every write.
//
// SECURITY: the bot token never reaches the browser. Binding writes go only to
// the authorized admin endpoints and are validated live server-side.

import { useCallback, useEffect, useState } from "react";

import { api } from "@/api/client";
import {
  listOpsChats,
  LANGUAGE_DEFAULT,
  type ChatBinding,
} from "@/api/telegram";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import { TelegramChatBinding } from "@/components/TelegramChatBinding";
import { TelegramApprovers } from "@/components/TelegramApprovers";
import { TikTokAccountsCard } from "@/components/TikTokAccountsCard";
import { DataTable, StatusPill, ToneBadge, type Column } from "@/components/nexus";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface NamedEntity {
  id: string;
  name: string;
}

function kindTone(kind: ChatBinding["kind"]) {
  return kind === "admin" ? "brand" : kind === "agency" ? "profit" : "na";
}

export function OpsBotPage() {
  const [chats, setChats] = useState<ChatBinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Owner-name lookups (display only; tolerant — a failure degrades to ids).
  const [clientNames, setClientNames] = useState<Record<string, string>>({});
  const [agencyNames, setAgencyNames] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listOpsChats();
      setChats(res.chats ?? []);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api
      .get<{ clients: NamedEntity[] }>("/admin/clients")
      .then((res) =>
        setClientNames(
          Object.fromEntries((res.clients ?? []).map((c) => [c.id, c.name])),
        ),
      )
      .catch(() => {});
    api
      .get<{ agencies: NamedEntity[] }>("/admin/agencies")
      .then((res) =>
        setAgencyNames(
          Object.fromEntries((res.agencies ?? []).map((a) => [a.id, a.name])),
        ),
      )
      .catch(() => {});
  }, []);

  function ownerLabel(c: ChatBinding): string {
    if (c.kind === "admin") return "Admin ops";
    if (c.kind === "agency") {
      return c.agency_id ? (agencyNames[c.agency_id] ?? c.agency_id) : "—";
    }
    return c.client_id ? (clientNames[c.client_id] ?? c.client_id) : "—";
  }

  // The active admin-singleton binding (or null when none), fed to the editor
  // so it does not re-fetch the same list.
  const adminBinding =
    chats.find((c) => c.active && c.kind === "admin") ?? null;

  const columns: Column<ChatBinding>[] = [
    {
      key: "kind",
      header: "Kind",
      cell: (c) => (
        <ToneBadge tone={kindTone(c.kind)} className="capitalize">
          {c.kind}
        </ToneBadge>
      ),
    },
    {
      key: "owner",
      header: "Owner",
      cell: (c) => <span className="text-nx-text">{ownerLabel(c)}</span>,
    },
    {
      key: "title",
      header: "Chat",
      cell: (c) =>
        c.title ? (
          <span className="text-nx-text-2">{c.title}</span>
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "chat_id",
      header: "Chat ID",
      cell: (c) => (
        <code className="font-mono text-xs text-nx-text-2 tabular-nums">
          {c.chat_id}
        </code>
      ),
    },
    {
      key: "language",
      header: "Language",
      cell: (c) => (
        <ToneBadge tone="na" className="uppercase">
          {c.language || LANGUAGE_DEFAULT[c.kind]}
        </ToneBadge>
      ),
    },
    {
      key: "bot_status",
      header: "Bot",
      cell: (c) =>
        c.bot_status ? (
          <StatusPill
            tone={c.bot_status === "administrator" ? "credit" : "na"}
          >
            {c.bot_status}
          </StatusPill>
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "active",
      header: "Active",
      cell: (c) => (
        <StatusPill tone={c.active ? "credit" : "na"}>
          {c.active ? "active" : "inactive"}
        </StatusPill>
      ),
    },
    {
      key: "validated",
      header: "Validated",
      align: "right",
      cell: (c) => (
        <span className="text-xs text-nx-text-3 tabular-nums">
          {c.validated_at ? new Date(c.validated_at).toLocaleString() : "—"}
        </span>
      ),
    },
  ];

  return (
    <AdminShell>
      <div className="mb-5">
        <h2 className="font-heading text-lg font-semibold">Ops bot</h2>
        <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
          Telegram group chats the ops bot is bound to. Client and agency chats
          are bound on their detail screens; the admin ops chat is bound here.
        </p>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Admin ops chat</CardTitle>
            <CardDescription>
              The single admin-side Telegram group. Binding is gated on a live
              check that the bot can actually work in the chat.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <TelegramChatBinding
                kind="admin"
                currentBinding={adminBinding}
                onChanged={() => void load()}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Money-approval approvers</CardTitle>
            <CardDescription>
              The reviewed Telegram accounts allowed to confirm money (recharge)
              actions from the ops bot.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TelegramApprovers />
          </CardContent>
        </Card>

        <TikTokAccountsCard />

        <Card>
          <CardHeader>
            <CardTitle>All chat bindings</CardTitle>
            <CardDescription>
              Every binding the backend knows about, including inactive
              (replaced or unbound) rows.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {error && (
              <Alert variant="destructive">
                <AlertTitle>Could not load chat bindings</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {loading ? (
              <div className="grid gap-2">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            ) : (
              <DataTable
                columns={columns}
                rows={chats}
                rowKey={(c) => `${c.kind}:${c.chat_id}:${c.created_at}`}
                empty="No Telegram chats bound yet."
              />
            )}
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}
